import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Plus,
  PackageOpen,
  IndianRupee,
  Clock,
  Search,
  ArrowUpRight,
  MoreHorizontal,
} from 'lucide-react';
import { DataTable, type Column, type TableDensity } from '@/components/common/DataTable';
import { DensityToggle } from '@/components/common/DensityToggle';
import { ListPageHeader } from '@/components/common/ListPageHeader';
import { ErrorState } from '@/components/common/ErrorState';
import { KpiCard } from '@/components/common/KpiCard';
import { StatusTabs, type StatusTabOption } from '@/components/common/StatusTabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { purchaseOrdersService } from '@/services/purchase-orders';
import type { PurchaseOrder, PurchaseOrderStatus } from '@/types/purchase-orders';
import { fmt } from '@/lib/format';
import { useDebounced } from '@/hooks/useDebounced';
import { PurchaseOrderDetailSheet } from './PurchaseOrderDetailSheet';
import { PurchaseOrderFormSheet } from './PurchaseOrderFormSheet';

type StatusFilter = 'all' | PurchaseOrderStatus;

const STATUS_LABEL: Record<PurchaseOrderStatus, string> = {
  DRAFT: 'Draft',
  ISSUED: 'Issued',
  PARTIALLY_RECEIVED: 'Partial',
  RECEIVED: 'Received',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

const STATUS_TONE: Record<PurchaseOrderStatus, 'muted' | 'success' | 'warning' | 'destructive'> = {
  DRAFT: 'muted',
  ISSUED: 'warning',
  PARTIALLY_RECEIVED: 'warning',
  RECEIVED: 'success',
  CLOSED: 'success',
  CANCELLED: 'destructive',
};

export function PurchaseOrdersPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<PurchaseOrder | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [density, setDensity] = useState<TableDensity>('compact');
  const debounced = useDebounced(search);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['purchase-orders', { q: debounced }],
    queryFn: () =>
      purchaseOrdersService.list({ search: debounced || undefined, pageSize: 200 }),
  });

  const allRows = useMemo(() => data?.rows ?? [], [data]);

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = {
      all: allRows.length,
      DRAFT: 0,
      ISSUED: 0,
      PARTIALLY_RECEIVED: 0,
      RECEIVED: 0,
      CLOSED: 0,
      CANCELLED: 0,
    };
    for (const r of allRows) c[r.status] += 1;
    return c;
  }, [allRows]);

  const filtered = useMemo(() => {
    if (filter === 'all') return allRows;
    return allRows.filter((r) => r.status === filter);
  }, [allRows, filter]);

  const summary = useMemo(() => {
    const open = allRows.filter(
      (r) => !['RECEIVED', 'CLOSED', 'CANCELLED'].includes(r.status),
    );
    return {
      openCount: open.length,
      openValue: open.reduce((s, r) => s + Number(r.totalAmount ?? 0), 0),
      partialCount: counts.PARTIALLY_RECEIVED,
    };
  }, [allRows, counts.PARTIALLY_RECEIVED]);

  const tabs: StatusTabOption[] = [
    { value: 'all',                label: 'All',       count: counts.all },
    { value: 'DRAFT',              label: 'Draft',     count: counts.DRAFT },
    { value: 'ISSUED',             label: 'Issued',    count: counts.ISSUED },
    { value: 'PARTIALLY_RECEIVED', label: 'Partial',   count: counts.PARTIALLY_RECEIVED },
    { value: 'RECEIVED',           label: 'Received',  count: counts.RECEIVED },
    { value: 'CLOSED',             label: 'Closed',    count: counts.CLOSED },
    { value: 'CANCELLED',          label: 'Cancelled', count: counts.CANCELLED },
  ];

  const columns: Column<PurchaseOrder>[] = [
    {
      key: 'po',
      header: 'PO #',
      width: '170px',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="font-mono text-[12px] font-medium text-foreground">{r.poNumber}</div>
          <div className="text-[10.5px] text-muted-foreground">{fmt.relative(r.createdAt)}</div>
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'PO date',
      width: '180px',
      cell: (r) => (
        <span className="font-mono text-[11.5px] tabular-nums text-muted-foreground">
          {fmt.dateTime(r.createdAt)}
        </span>
      ),
    },
    {
      key: 'vendor',
      header: 'Vendor',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="font-medium text-foreground">{r.vendor.name}</div>
          <div className="font-mono text-[10.5px] text-muted-foreground">{r.vendor.code}</div>
        </div>
      ),
    },
    {
      key: 'warehouse',
      header: 'Warehouse',
      width: '160px',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="text-[12.5px] text-foreground">{r.warehouse.name ?? r.warehouse.code}</div>
          <div className="font-mono text-[10.5px] text-muted-foreground">{r.warehouse.code}</div>
        </div>
      ),
    },
    {
      key: 'lines',
      header: 'Lines',
      align: 'right',
      width: '70px',
      cell: (r) => (
        <span className="font-mono text-[12.5px] tabular-nums text-muted-foreground">
          {r.items.length || '—'}
        </span>
      ),
    },
    {
      key: 'received',
      header: 'Received',
      align: 'right',
      width: '110px',
      cell: (r) => {
        const ordered = r.items.reduce((s, l) => s + Number(l.quantity), 0);
        const got = r.items.reduce((s, l) => s + Number(l.receivedQuantity), 0);
        return (
          <span className="font-mono text-[12.5px] tabular-nums">
            {fmt.number(got)} / {fmt.number(ordered)}
          </span>
        );
      },
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      width: '120px',
      cell: (r) => (
        <span className="font-mono text-[12.5px] tabular-nums">
          {r.totalAmount != null ? fmt.inr(r.totalAmount) : '—'}
        </span>
      ),
    },
    {
      key: 'eta',
      header: 'Expected delivery',
      width: '180px',
      cell: (r) =>
        r.expectedDeliveryAt ? (
          <span className="font-mono text-[11.5px] tabular-nums text-muted-foreground">
            {fmt.dateTime(r.expectedDeliveryAt)}
          </span>
        ) : (
          <span className="text-[11.5px] text-muted-foreground">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '120px',
      cell: (r) => (
        <Badge variant={STATUS_TONE[r.status]} className="text-[10px]">
          {STATUS_LABEL[r.status]}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '90px',
      hoverReveal: true,
      cell: (r) => (
        <div className="flex items-center justify-end gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={() => setSelected(r)}
              >
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Open</TooltipContent>
          </Tooltip>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setSelected(r)}>Open detail</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4">
        <ListPageHeader
          title="Purchase Orders"
          count={counts.all}
          actions={
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-3.5 w-3.5" />
              New PO
            </Button>
          }
        />

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <KpiCard label="Open POs" value={fmt.number(summary.openCount)} tone="primary" icon={PackageOpen} />
          <KpiCard label="Open value" value={fmt.inr(summary.openValue)} tone="default" icon={IndianRupee} />
          <KpiCard
            label="Partially received"
            value={fmt.number(summary.partialCount)}
            tone={summary.partialCount ? 'warning' : 'default'}
            icon={Clock}
          />
        </div>

        <StatusTabs value={filter} onChange={(v) => setFilter(v as StatusFilter)} options={tabs} />

        <div className="flex items-center justify-between gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search PO # …"
              className="h-8 pl-8 text-[13px]"
            />
          </div>
          <DensityToggle value={density} onChange={setDensity} />
        </div>

        {error ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            onRowClick={setSelected}
            density={density}
            emptyTitle={
              filter === 'all'
                ? 'No purchase orders yet'
                : `No ${STATUS_LABEL[filter as PurchaseOrderStatus].toLowerCase()} POs`
            }
            emptyDescription={
              filter === 'all'
                ? 'Issue your first PO to a vendor to track upcoming receipts.'
                : 'Try a different status filter.'
            }
            emptyAction={
              filter === 'all' ? (
                <Button size="sm" onClick={() => setCreateOpen(true)}>
                  <Plus className="h-3.5 w-3.5" />
                  New PO
                </Button>
              ) : null
            }
          />
        )}

        <PurchaseOrderDetailSheet
          po={selected}
          open={!!selected}
          onOpenChange={(o) => !o && setSelected(null)}
        />

        <PurchaseOrderFormSheet open={createOpen} onOpenChange={setCreateOpen} />
      </div>
    </TooltipProvider>
  );
}
